/**
 * components/catalog/CategorySuggestions.js
 * ─────────────────────────────────────────────────────────────────────────────
 * "Suggested: Detergent · Cleaning · Laundry" under a product form's category
 * picker. One click adds the category; nothing is ever chosen for the vendor.
 *
 * The scoring is the server's (GET /category/recommend, see
 * services/catalog/categoryRecommender.js): the words in the name, the
 * categories similar products already sit in, the unit, and where this vendor
 * usually files things. This component only asks, debounced, and shows the
 * answer. A failed or slow request shows nothing; it never blocks the form.
 *
 * When the name carries a size ("Omo 1kg") and the form has no unit yet, it
 * also offers "Use 1 Kg", in the preferred spelling, if the form passes
 * `onApplyUnit`.
 *
 *   <CategorySuggestions
 *     name={product.name}
 *     unitId={product.unit_id}
 *     vendorId={brandId}
 *     categories={categoryOptions}      // the full /category/get-all list
 *     selected={selectedCategory}       // category objects or ids
 *     onAdd={(category) => setSelectedCategory((s) => [...s, category])}
 *     onApplyUnit={({ _id, measurements }) => ...}   // optional
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { dashboardApi } from "../../api/index.js";
import "./catalog.css";

const DEBOUNCE_MS = 450;
const idOf = (v) => String(v?._id ?? v?.value ?? v ?? "");

export default function CategorySuggestions({
  name = "",
  unitId = null,
  vendorId = null,
  categories = [],
  selected = [],
  onAdd,
  onApplyUnit,
  disabled = false,
}) {
  const [result, setResult] = useState({ suggestions: [], unit: null });
  const seq = useRef(0);

  const selectedIds = useMemo(
    () => new Set((Array.isArray(selected) ? selected : [selected]).map(idOf).filter(Boolean)),
    [selected]
  );
  const exclude = useMemo(() => [...selectedIds].sort().join(","), [selectedIds]);
  const trimmed = String(name || "").trim();

  useEffect(() => {
    if (trimmed.length < 3) {
      setResult({ suggestions: [], unit: null });
      return undefined;
    }
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await dashboardApi.get("/category/recommend", {
          params: {
            name: trimmed,
            ...(unitId ? { unitId: idOf(unitId) } : {}),
            ...(vendorId ? { vendorId: idOf(vendorId) } : {}),
            ...(exclude ? { exclude } : {}),
            limit: 3,
          },
        });
        if (mine === seq.current) {
          setResult({
            suggestions: Array.isArray(data?.suggestions) ? data.suggestions : [],
            unit: data?.unit || null,
          });
        }
      } catch (_) {
        if (mine === seq.current) setResult({ suggestions: [], unit: null });
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed, unitId, vendorId, exclude]);

  const byId = useMemo(
    () => new Map((Array.isArray(categories) ? categories : []).map((c) => [String(c._id), c])),
    [categories]
  );
  const chips = result.suggestions.filter((s) => !selectedIds.has(String(s._id)));
  const unitOffer = onApplyUnit && result.unit && !unitId ? result.unit : null;

  if (!chips.length && !unitOffer) return null;

  return (
    <div className="lk-suggest" role="group" aria-label="Suggested categories">
      <span className="lk-suggest__label">
        <i className="far fa-lightbulb" aria-hidden="true" />
        Suggested
      </span>
      {chips.map((s) => {
        const full = byId.get(String(s._id)) || { _id: s._id, category: s.category };
        const why = (s.reasons || []).join(". ");
        return (
          <button
            key={String(s._id)}
            type="button"
            className="lk-suggest__chip"
            disabled={disabled}
            title={why ? `${why}. Click to add.` : "Click to add."}
            onClick={() => onAdd && onAdd(full)}
          >
            <i className="fas fa-plus lk-suggest__plus" aria-hidden="true" />
            {s.category}
            {s.groupLabel ? <span className="lk-suggest__group">{s.groupLabel}</span> : null}
          </button>
        );
      })}
      {unitOffer ? (
        <button
          type="button"
          className="lk-suggest__chip lk-suggest__chip--unit"
          disabled={disabled}
          title={`Read "${unitOffer.text}" from the name. Click to set the measurement and unit.`}
          onClick={() => onApplyUnit(unitOffer)}
        >
          <i className="fas fa-ruler-combined lk-suggest__plus" aria-hidden="true" />
          Use {unitOffer.measurements} {unitOffer.unit}
        </button>
      ) : null}
    </div>
  );
}
